import { deepClone, exists, filterDuplicates, ImplementationMissingException, MUSTNeverHappenException, reduceObject, removeFromArrayByIndex, sortObject } from '@nu-art/ts-common';
import { DBDef_Tag, DBDef_Value, DBDef_Variable, resolveValueDisplay, } from '@app/hcs-shared';
import { referencablePropType_Compose, referencablePropType_Inherit, referencableType_MultiValue, } from '@app/hcs-shared/hcs-v2';
import { ModuleFE_Tag } from '../../tag/index.js';
import { ModuleFE_Variable } from '../../variable/index.js';
import { ModuleFE_Value } from '../../value/index.js';
const propOrder = ['_id', 'label', 'extends', 'type', 'prototypes', 'properties', 'dbRefs'];
function resolveDBPointerDisplay(pointer) {
    switch (pointer.dbKey) {
        case DBDef_Tag.dbKey:
            return 'Tag - ' + (ModuleFE_Tag.cache.unique(pointer.id)?.label ?? 'N/A');
        case DBDef_Variable.dbKey:
            return 'Variable - ' + (ModuleFE_Variable.cache.unique(pointer.id)?.name ?? 'N/A');
        case DBDef_Value.dbKey: {
            const value = ModuleFE_Value.cache.unique(pointer.id);
            return 'Value - ' + (value ? resolveValueDisplay(value) : 'N/A');
        }
        default: {
            throw new ImplementationMissingException(`No implementation for dbKey ${pointer.dbKey}`);
        }
    }
}
export function translateReferencable(referencable, mapIdToReferencable) {
    const convert = (id) => `_id(${mapIdToReferencable[id].label})`;
    //Convert item id
    const id = convert(referencable._id);
    //Convert extends
    const extend = referencable.extends.map(id => convert(id));
    //Convert props
    const prototypes = referencable.prototypes.map(prototype => `${prototype.types.join(' ')} ${convert(prototype.id)}`);
    //Convert properties
    const properties = reduceObject(referencable.properties, {}, (acc, key, value) => {
        acc[convert(key)] = value.map(id => convert(id));
        delete acc[key];
        return acc;
    });
    //Convert dbRefs
    const dbRefs = referencable.dbRefs?.map(resolveDBPointerDisplay);
    //return sorted translated referencable
    return sortObject({
        _id: id,
        label: referencable.label,
        type: referencable.type,
        extends: extend,
        prototypes,
        properties,
        dbRefs,
    }, key => propOrder.indexOf(key));
}
export function resolveReferencable(referencable, mapIdToReferencable) {
    function collectParents(referencable, acc = []) {
        if (!referencable.extends.length)
            return acc;
        const parents = referencable.extends.map(id => mapIdToReferencable[id]);
        return parents.reduce((acc, parent) => {
            const entity = {
                _id: parent._id,
                props: parent.prototypes.filter(prop => prop.types.includes('inherit')).map(prop => ({ _id: prop.id, parentId: parent._id })),
                properties: reduceObject(parent.properties, {}, (acc, key, values) => {
                    acc[key] = values.map((value => ({ valueId: value, parentId: parent._id })));
                    return acc;
                })
            };
            acc.push(entity);
            acc = collectParents(parent, acc);
            return acc;
        }, acc);
    }
    const parents = collectParents(referencable);
    const fullRef = deepClone(referencable);
    fullRef.extends = parents.map(parent => parent._id);
    return fullRef;
}
export function collectParentStacks(current, allEntities, visited = new Set()) {
    const nextLayer = [];
    const thisLayer = [];
    for (const entity of current) {
        if (visited.has(entity._id))
            throw new Error(`Cycle detected at entity: ${entity._id}`);
        thisLayer.push(entity);
        for (const parentId of entity.extends || []) {
            const parent = allEntities[parentId];
            if (!parent)
                throw new Error(`Missing parent entity with id: ${parentId}`);
            nextLayer.push(parent);
        }
    }
    if (nextLayer.length === 0)
        return [thisLayer];
    // 👇 Use a *new* visited set per level
    const nextVisited = new Set(visited);
    for (const e of thisLayer)
        nextVisited.add(e._id);
    const parentLayers = collectParentStacks(filterDuplicates(nextLayer, ref => ref._id), allEntities, nextVisited);
    parentLayers.push(thisLayer);
    return parentLayers;
}
export function collectComposingTargets(target, allEntities, visited = new Set()) {
    if (visited.has(target._id))
        return [];
    visited.add(target._id);
    const result = [];
    for (const propValues of Object.values(target.properties)) {
        for (const refId of propValues) {
            if (visited.has(refId))
                continue;
            const referenced = allEntities[refId];
            if (!referenced)
                continue; // optionally throw or log if strict
            result.push(referenced);
            const nested = collectComposingTargets(referenced, allEntities, visited);
            result.push(...nested);
        }
    }
    return result;
}
export function qualifyReferencable(root, allEntities) {
    const flattened = {
        _id: root._id,
        label: root.label,
        type: root.type, // ✅ preserve only root type
        dbRefs: root.dbRefs,
        extends: [],
        prototypes: [],
        properties: {},
    };
    // 1. Collect ancestry and composition
    const parents = collectParentStacks([root], allEntities).flat();
    parents.reverse();
    removeFromArrayByIndex(parents, 0);
    const composing = collectComposingTargets(root, allEntities);
    // 2. Process prototypess
    const parentsPrototypes = parents.flatMap(r => r.prototypes)
        .filter(r => r.types.includes(referencablePropType_Inherit)).map(p => {
        return { ...p, type: [referencablePropType_Inherit] };
    });
    const composingPrototypes = composing.flatMap(r => r.prototypes)
        .filter(r => r.types.includes(referencablePropType_Compose)).map(p => {
        return { ...p, type: [referencablePropType_Inherit] };
    });
    flattened.prototypes = filterDuplicates([
        ...root.prototypes,
        ...parentsPrototypes,
        ...composingPrototypes
    ], p => p.id);
    const reducer = (properties, key, values) => {
        if (!exists(properties[key]))
            properties[key] = [];
        properties[key].push(...values);
        properties[key] = filterDuplicates(properties[key]);
        const entity = allEntities[key];
        if (properties[key].length > 1 && !entity.type.includes(referencableType_MultiValue))
            throw new MUSTNeverHappenException(`Too many values for entity: (${entity.label}|${entity._id})`);
        return properties;
    };
    flattened.properties = reduceObject(root.properties, flattened.properties, reducer);
    parents.forEach(parent => reduceObject(parent.properties, flattened.properties, reducer));
    composing.forEach(parent => reduceObject(parent.properties, flattened.properties, reducer));
    return flattened;
}
//# sourceMappingURL=utils.js.map